import { useSearchParams } from "react-router-dom";
import { Database } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ErrorCard, Field, LoadingBlock, PageHeader, Pill } from "../components/jamora-admin/AdminBits";
import { AdminTable, FilterTabs, ListFrame, MoreFooter } from "../components/jamora-admin/ListBits";
import useAdminFetch from "../hooks/useAdminFetch";
import useAdminList from "../hooks/useAdminList";
import { RUN_STATUS, RUN_TYPE_LABELS, formatCount, formatDuration } from "../lib/jamoraAdmin";
import { patchedSearch } from "../lib/searchParams";
import { formatDateTime } from "../lib/tickets";

const STATUS_TABS = [["", "Toutes"], ["running", "En cours"], ["success", "Succès"], ["failed", "Échecs"]];
const KIND_TABS = [["", "Réelles et simulations"], ["false", "Réelles"], ["true", "Simulations"]];
const COUNTER_LABELS = {
  total_source: "Produits lus", created_count: "Créés", existing_count: "Déjà à jour", updated_count: "Mis à jour", would_update_count: "Seraient mis à jour (simulation)",
  ignored_count: "Ignorés", error_count: "En erreur", grossistes_calls: "Appels aux grossistes", grossistes_no_offer: "Sans offre grossiste", grossistes_errors: "Erreurs grossistes",
  n1_resolved: "Rapprochés (niveau 1)", n2_resolved: "Rapprochés (niveau 2)", supplier_tiebreak_resolved: "Départagés par le fournisseur", dosage_veto: "Refusés (dosage)",
  name_veto: "Refusés (nom)", sentinel_rejected: "Codes sentinelles rejetés", conflicts: "Conflits", final_non_matche: "Non rapprochés", final_cip_ambigu: "CIP ambigus",
};

// The platform-wide resynchronisation settings (hour, window, delay between calls), as Jamora holds them.
function OnepharmaSettingsLine() {
  const { phase, data } = useAdminFetch("/onepharma-settings");
  if (phase !== "ready") return null;
  return (
    <p className="text-xs text-slate-500" data-testid="onepharma-settings">
      Resynchronisation nocturne : {data.resync_time ? `à ${data.resync_time}` : "heure non définie"} · fenêtre de {formatCount(data.window_days)} jour(s) · {formatCount(data.call_delay_ms)} ms entre deux appels
    </p>
  );
}

function RunDialog({ runId, onClose }) {
  const { phase, data: run, error, reload } = useAdminFetch(runId ? `/onepharma-runs/${runId}` : null, undefined, { enabled: !!runId });
  const [label, tone] = run ? RUN_STATUS[run.status] || [run.status, "slate"] : ["", "slate"];
  return (
    <Dialog open={!!runId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="run-dialog">
        <DialogHeader>
          <DialogTitle className="text-left">{run ? `${run.pharmacy.name} · ${RUN_TYPE_LABELS[run.type] || run.type}` : "Synchronisation"}</DialogTitle>
          <DialogDescription className="text-left">{run ? formatDateTime(run.started_at) : "Chargement…"}</DialogDescription>
        </DialogHeader>
        {phase === "error" && <ErrorCard message={error} onRetry={reload} testid="run-error" />}
        {(phase === "loading" || phase === "idle") && <LoadingBlock rows={3} />}
        {run && (
          <div className="space-y-4 text-sm">
            <dl className="divide-y divide-slate-100">
              <Field label="Statut"><span className="inline-flex gap-1"><Pill tone={tone}>{label}</Pill>{run.dry_run && <Pill tone="violet">Simulation</Pill>}</span></Field>
              <Field label="Durée">{formatDuration(run.duration_ms)}</Field>
              <Field label="Vérification">{run.verified ? "Effectuée" : "Non effectuée"}</Field>
              {run.error_message && <Field label="Message d'erreur"><span className="text-red-700 whitespace-pre-wrap" data-testid="run-error-message">{run.error_message}</span></Field>}
            </dl>
            <section>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Compteurs</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6" data-testid="run-counters">
                {Object.entries(COUNTER_LABELS).filter(([key]) => run.counters[key] !== undefined).map(([key, text]) => (
                  <div key={key} className="flex items-baseline justify-between gap-3 border-b border-slate-100 py-1"><dt className="text-slate-500">{text}</dt><dd className="font-medium text-slate-900">{formatCount(run.counters[key])}</dd></div>
                ))}
              </dl>
            </section>
            {Object.keys(run.reason_codes).length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">Motifs de traitement</h3>
                <ul className="flex flex-wrap gap-1.5" data-testid="run-reasons">{Object.entries(run.reason_codes).sort((a, b) => b[1] - a[1]).map(([code, count]) => <li key={code}><Pill>{code} · {formatCount(count)}</Pill></li>)}</ul>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// The synchronisations from the Onepharma software, newest first (read only; the detail opens a window).
export default function JamoraAdminOnepharma() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const dry = params.get("dry_run") || "";
  const open = Number(params.get("run")) || null;
  const list = useAdminList("/onepharma-runs", { status, dry_run: dry });
  const update = (patch) => setParams(patchedSearch(patch), { replace: true });

  const columns = [
    { id: "pharmacy", label: "Pharmacie", primary: true, cell: (r) => r.pharmacy.name },
    { id: "type", label: "Type", cell: (r) => <span>{RUN_TYPE_LABELS[r.type] || r.type}{r.dry_run && <span className="ml-1.5 align-middle"><Pill tone="violet">Simulation</Pill></span>}</span> },
    { id: "status", label: "Statut", cell: (r) => { const [text, tone] = RUN_STATUS[r.status] || [r.status, "slate"]; return <Pill tone={tone}>{text}</Pill>; } },
    { id: "started", label: "Début", cell: (r) => formatDateTime(r.started_at) },
    { id: "duration", label: "Durée", hide: "lg", cell: (r) => formatDuration(r.duration_ms) },
    { id: "counts", label: "Créés / mis à jour / erreurs", hide: "lg", cell: (r) => `${formatCount(r.created)} / ${formatCount(r.updated)} / ${formatCount(r.errors)}` },
  ];
  return (
    <div className="space-y-4" data-testid="admin-onepharma">
      <PageHeader title="Synchronisations Onepharma" description="Les migrations et resynchronisations du stock depuis le logiciel Onepharma, les plus récentes d'abord." />
      <OnepharmaSettingsLine />
      <FilterTabs label="Statut" tabs={STATUS_TABS} value={status} onChange={(v) => update({ status: v })} testid="onepharma-status" />
      <FilterTabs label="Type" tabs={KIND_TABS} value={dry} onChange={(v) => update({ dry_run: v })} testid="onepharma-kind" />
      <ListFrame list={list} empty={{ icon: Database, title: "Aucune synchronisation", hint: status || dry ? "Retirez un filtre pour en voir davantage." : "Les synchronisations Onepharma apparaîtront ici.", testid: "onepharma-empty" }}>
        <AdminTable columns={columns} rows={list.items} rowKey={(r) => r.id} testid="onepharma-table" onRowClick={(r) => update({ run: String(r.id) })} />
        <MoreFooter list={list} noun="synchronisation(s)" testid="onepharma" />
      </ListFrame>
      <RunDialog runId={open} onClose={() => update({ run: "" })} />
    </div>
  );
}
